import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FooterTabBarLayout from '../../layouts/FooterTabBarLayout';
import HeaderBar from '../../layouts/HeaderBar';
import { getMeetingMap } from '../../../api/MeetingApi';
import { IoSearchOutline } from 'react-icons/io5';
import NotificationIcon from '../../layouts/NotificationIcon';
import useAuthStore from '../../../store/authStore';

const ExplorePage = () => {
  const navigate = useNavigate();
  const { member } = useAuthStore();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);

  // authStore의 멤버 위치를 기본값으로 사용
  const [currentPosition, setCurrentPosition] = useState({
    lat: member?.latitude || 37.5666805,
    lng: member?.longitude || 126.9784147
  });

  const [allMeetings, setAllMeetings] = useState([]); // 전체 모임 데이터
  const [filteredMeetings, setFilteredMeetings] = useState([]); // 필터링된 모임 데이터
  const [loading, setLoading] = useState(false);

  // 시간대 필터 상태
  const [selectedDate, setSelectedDate] = useState('all');
  const [selectedTime, setSelectedTime] = useState('all');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);

  // 멤버 정보 변경 시 현재 위치 업데이트
  useEffect(() => {
    if (member?.latitude && member?.longitude) {
      setCurrentPosition({
        lat: member.latitude,
        lng: member.longitude
      });
    }
  }, [member]);

  // 헤더 오른쪽 아이콘 설정
  const rightIcons = [
    {
      icon: <IoSearchOutline size={24} className="text-gray-600" />,
      onClick: () => navigate('/app/search'),
      title: '검색'
    },
    {
      icon: <NotificationIcon />,
      onClick: () => {},
      title: '알림'
    }
  ];

  // 모임 필터링 함수
  const filterMeetings = (meetings) => {
    const now = new Date();

    return meetings.filter(meeting => {
      const meetingDate = new Date(meeting.scheduledAt);

      // 날짜 필터
      let dateMatch = false;
      switch (selectedDate) {
        case 'today':
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          dateMatch = meetingDate >= today && meetingDate < tomorrow;
          break;
        case 'tomorrow':
          const tomorrowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
          const tomorrowEnd = new Date(tomorrowStart);
          tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);
          dateMatch = meetingDate >= tomorrowStart && meetingDate < tomorrowEnd;
          break;
        case 'dayAfterTomorrow':
          const dayAfterTomorrowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2);
          const dayAfterTomorrowEnd = new Date(dayAfterTomorrowStart);
          dayAfterTomorrowEnd.setDate(dayAfterTomorrowEnd.getDate() + 1);
          dateMatch = meetingDate >= dayAfterTomorrowStart && meetingDate < dayAfterTomorrowEnd;
          break;
        case 'all':
          dateMatch = meetingDate >= now; // 현재 시간 이후의 모든 모임
          break;
        default:
          dateMatch = true;
      }

      if (!dateMatch) return false;

      // 시간대 필터 (전체 날짜가 아닌 경우에만 적용)
      if (selectedDate !== 'all' && selectedTime !== 'all') {
        const hour = meetingDate.getHours();
        let timeMatch = false;

        switch (selectedTime) {
          case 'morning': // 05:00~10:59
            timeMatch = hour >= 5 && hour <= 10;
            break;
          case 'lunch': // 11:00~15:59
            timeMatch = hour >= 11 && hour <= 15;
            break;
          case 'dinner': // 16:00~20:59
            timeMatch = hour >= 16 && hour <= 20;
            break;
          case 'latenight': // 21:00~04:59 (다음날 새벽 포함)
            timeMatch = hour >= 21 || hour <= 4;
            break;
          default:
            timeMatch = true;
        }

        return timeMatch;
      }

      return true;
    });
  };

  // 지도 초기화
  useEffect(() => {
    if (!window.kakao || !window.kakao.maps) {
      console.error('카카오맵 API가 로드되지 않았습니다.');
      return;
    }

    window.kakao.maps.load(() => {
      const container = mapRef.current;
      const options = {
        center: new window.kakao.maps.LatLng(currentPosition.lat, currentPosition.lng),
        level: 4
      };

      const map = new window.kakao.maps.Map(container, options);
      mapInstanceRef.current = map;

      // 인포윈도우 생성
      infoWindowRef.current = new window.kakao.maps.InfoWindow({
        zIndex: 1
      });

      // 지도 클릭 시 인포윈도우 닫기
      window.kakao.maps.event.addListener(map, 'click', function() {
        infoWindowRef.current.close();
      });

      // 초기 모임 데이터 로드
      loadMeetings(currentPosition.lat, currentPosition.lng);
    });
  }, []);

  // 날짜/시간 필터 변경 시 데이터 다시 필터링
  useEffect(() => {
    const filtered = filterMeetings(allMeetings);
    setFilteredMeetings(filtered);
    displayMarkers(filtered);
  }, [selectedDate, selectedTime, allMeetings]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setShowDateDropdown(false);
        setShowTimeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 모임 데이터 로드 함수
  const loadMeetings = async (lat, lng) => {
    setLoading(true);
    try {
      const response = await getMeetingMap(lat, lng);
      const meetingList = response.meetingMapDtoList || [];
      setAllMeetings(meetingList);

      // 초기 필터링 적용
      const filtered = filterMeetings(meetingList);
      setFilteredMeetings(filtered);
      displayMarkers(filtered);
    } catch (error) {
      console.error('모임 데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  // 날짜 선택 핸들러
  const handleDateChange = (date) => {
    setSelectedDate(date);
    // '전체'를 선택하면 시간대는 자동으로 '전체'로 고정
    if (date === 'all') {
      setSelectedTime('all');
    }
  };

  // 시간 선택 핸들러
  const handleTimeChange = (time) => {
    setSelectedTime(time);
  };

  // 현재 위치로 재검색 함수
  const handleRefreshLocation = () => {
    if (!mapInstanceRef.current) return;

    const center = mapInstanceRef.current.getCenter();
    const newLat = center.getLat();
    const newLng = center.getLng();

    setCurrentPosition({ lat: newLat, lng: newLng });
    loadMeetings(newLat, newLng);
  };

  // 마커 표시 함수
  const displayMarkers = (meetingList) => {
    if (!mapInstanceRef.current) return;

    // 기존 마커 제거
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    meetingList.forEach(meeting => {
      // 파트너 장소인지에 따라 다른 마커 이미지 사용
      const markerImageSrc = meeting.isPartnerPlace
        ? createSpecialMarkerImage()
        : 'https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/marker_red.png';

      const markerImage = new window.kakao.maps.MarkerImage(
        markerImageSrc,
        new window.kakao.maps.Size(24, 35)
      );

      const marker = new window.kakao.maps.Marker({
        position: new window.kakao.maps.LatLng(meeting.placeLatitude || currentPosition.lat, meeting.placeLongitude || currentPosition.lng),
        image: meeting.isPartnerPlace ? markerImage : null,
        title: meeting.title
      });

      marker.setMap(mapInstanceRef.current);
      markersRef.current.push(marker);

      // 마커 클릭 이벤트
      window.kakao.maps.event.addListener(marker, 'click', () => {
        const content = createInfoWindowContent(meeting);
        infoWindowRef.current.setContent(content);
        infoWindowRef.current.open(mapInstanceRef.current, marker);
      });
    });
  };

  // 스페셜 마커 이미지 생성 (파란색과 초록색 신비한 그라데이션)
  const createSpecialMarkerImage = () => {
    const svgContent = `
      <svg width="24" height="35" viewBox="0 0 24 35" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="mystical" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#001f3f"/>
            <stop offset="20%" style="stop-color:#003d7a"/>
            <stop offset="40%" style="stop-color:#0074D9"/>
            <stop offset="60%" style="stop-color:#39CCCC"/>
            <stop offset="80%" style="stop-color:#2ECC40"/>
            <stop offset="100%" style="stop-color:#3D9970"/>
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <path d="M12 0C5.4 0 0 5.4 0 12c0 12 12 23 12 23s12-11 12-23C24 5.4 18.6 0 12 0z" 
              fill="url(#mystical)" 
              stroke="#fff" 
              stroke-width="2"
              filter="url(#glow)"/>
        <circle cx="12" cy="12" r="6" fill="#fff" opacity="0.9"/>
        <text x="12" y="16" text-anchor="middle" font-family="Arial" font-size="8" font-weight="bold" fill="#0074D9">✦</text>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgContent)))}`;
  };

  // 인포윈도우 콘텐츠 생성 (버튼처럼 보이는 디자인)
  const createInfoWindowContent = (meeting) => {
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleString('ko-KR', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return `
      <div style="
        padding: 16px;
        min-width: 240px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.1);
        cursor: pointer;
      " onclick="window.navigateToMeeting(${meeting.meetingId})">
        
        <!-- 메인 콘텐츠 -->
        <div>
          <div style="
            color: #1a1a1a;
            font-weight: bold;
            font-size: 16px;
            margin-bottom: 12px;
          ">
            ${meeting.title}
          </div>
          
          <div style="
            color: #666;
            font-size: 13px;
            margin-bottom: 6px;
            display: flex;
            align-items: center;
          ">
            <span style="margin-right: 8px;">📅</span>
            ${formatDate(meeting.scheduledAt)}
          </div>
          
          <div style="
            color: #666;
            font-size: 13px;
            margin-bottom: 12px;
            display: flex;
            align-items: center;
          ">
            <span style="margin-right: 8px;">📍</span>
            ${meeting.placeName}
          </div>
          
          ${meeting.isPartnerPlace ? `
            <div style="
              color: #0074D9;
              font-size: 11px;
              margin-bottom: 12px;
              display: flex;
              align-items: center;
            ">
              <span style="margin-right: 4px;">✦</span>
              제휴 매장 혜택
            </div>
          ` : ''}
          
          <!-- 클릭 유도 버튼 -->
          <div style="
            background: #f8f9fa;
            color: #495057;
            font-size: 12px;
            padding: 8px 16px;
            text-align: center;
            font-weight: 500;
            border: 1px solid #dee2e6;
          ">
            모임 상세보기 →
          </div>
        </div>
      </div>
    `;
  };

  // 전역 네비게이션 함수 설정
  useEffect(() => {
    window.navigateToMeeting = (meetingId) => {
      navigate(`/app/meetings/${meetingId}`);
    };

    return () => {
      delete window.navigateToMeeting;
    };
  }, [navigate]);

  return (
    <FooterTabBarLayout>
      <div className="flex flex-col h-full">
        <HeaderBar
          title="모임 탐색"
          rightIcons={rightIcons}
        />

        <div className="relative flex-1 overflow-hidden">
          {/* 지도 컨테이너 */}
          <div
            ref={mapRef}
            className="absolute inset-0"
          />

          {/* 재검색 버튼 */}
          <button
            onClick={handleRefreshLocation}
            disabled={loading}
            className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white border border-gray-300 rounded-lg px-4 py-2 shadow-lg text-sm font-medium text-gray-700 active:bg-gray-50 disabled:opacity-50 z-10"
          >
            {loading ? '검색 중...' : '이 위치로 재검색'}
          </button>

          {/* 알약 형태 필터 */}
          <div className="absolute bottom-6 left-4 right-4 flex gap-3 z-10">
            {/* 날짜 선택 알약 */}
            <div className="relative flex-1 dropdown-container">
              {showDateDropdown && (
                <div className="absolute bottom-full mb-2 left-0 right-0 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                  {[
                    { value: 'today', label: '오늘', emoji: '🏞️' },
                    { value: 'tomorrow', label: '내일', emoji: '🌅' },
                    { value: 'dayAfterTomorrow', label: '모레', emoji: '🌄' },
                    { value: 'all', label: '일자 전체', emoji: '📅' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        handleDateChange(option.value);
                        setShowDateDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left flex items-center gap-3 active:bg-gray-50 transition-colors ${
                        selectedDate === option.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-lg">{option.emoji}</span>
                      <span className="font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* 날짜 알약 버튼 */}
              <button
                onClick={() => {
                  setShowDateDropdown(!showDateDropdown);
                  setShowTimeDropdown(false);
                }}
                className="w-full px-4 py-3 rounded-full flex items-center justify-center gap-2 text-gray-700 font-medium"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(255,255,255,0.4))',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)',
                }}
              >
                <span className="text-sm">
                  {selectedDate === 'today' && '🏞️ 오늘'}
                  {selectedDate === 'tomorrow' && '🌅 내일'}
                  {selectedDate === 'dayAfterTomorrow' && '🌄 모레'}
                  {selectedDate === 'all' && '📅 전체'}
                </span>
                <span className="text-xs text-gray-500 transform rotate-180">▼</span>
              </button>
            </div>

            {/* 시간 선택 알약 */}
            <div className="relative flex-1 dropdown-container">
              {showTimeDropdown && selectedDate !== 'all' && (
                <div className="absolute bottom-full mb-2 left-0 right-0 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                  {[
                    { value: 'morning', label: '아침', emoji: '🍞', time: '05-10시' },
                    { value: 'lunch', label: '점심', emoji: '🥘', time: '11-15시' },
                    { value: 'dinner', label: '저녁', emoji: '🍽️', time: '16-20시' },
                    { value: 'latenight', label: '야식', emoji: '🌙', time: '21-04시' },
                    { value: 'all', label: '시간 전체', emoji: '⏰', time: '' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        handleTimeChange(option.value);
                        setShowTimeDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left flex items-center justify-between active:bg-gray-50 transition-colors ${
                        selectedTime === option.value 
                          ? 'bg-amber-50 text-amber-600' 
                          : 'text-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{option.emoji}</span>
                        <span className="font-medium">{option.label}</span>
                      </div>
                      {option.time && (
                        <span className="text-xs text-gray-500">{option.time}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* 시간 알약 버튼 */}
              <button
                onClick={() => {
                  if (selectedDate !== 'all') {
                    setShowTimeDropdown(!showTimeDropdown);
                    setShowDateDropdown(false);
                  }
                }}
                disabled={selectedDate === 'all'}
                className={`w-full px-4 py-3 rounded-full flex items-center justify-center gap-2 font-medium ${
                  selectedDate === 'all' 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-gray-700'
                }`}
                style={{
                  background: selectedDate === 'all'
                    ? 'linear-gradient(135deg, rgba(156,163,175,0.5), rgba(156,163,175,0.3))'
                    : 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(255,255,255,0.4))',
                  backdropFilter: 'blur(10px)',
                  border: selectedDate === 'all'
                    ? '1px solid rgba(156,163,175,0.3)'
                    : '1px solid rgba(255,255,255,0.3)',
                  boxShadow: selectedDate === 'all'
                    ? '0 4px 16px rgba(156, 163, 175, 0.1)'
                    : '0 8px 32px rgba(31, 38, 135, 0.15)',
                }}
              >
                <span className="text-sm">
                  {selectedTime === 'morning' && '🍞 아침'}
                  {selectedTime === 'lunch' && '🥘 점심'}
                  {selectedTime === 'dinner' && '🍽️ 저녁'}
                  {selectedTime === 'latenight' && '🌙 야식'}
                  {selectedTime === 'all' && '⏰ 전체'}
                </span>
                <span className={`text-xs transform rotate-180 ${
                  selectedDate === 'all' ? 'text-gray-400' : 'text-gray-500'
                }`}>▼</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </FooterTabBarLayout>
  );
};

export default ExplorePage;
